// Playback speed for the custom VIPS control bar (hls_player_controller.js).
// The S3 standalone player (app/views/videos/hls_player.html.erb) inlines the
// same behavior, since it must stay self-contained: keep the two in step.
//
// The button cycles SPEEDS in order; step() moves through them sorted (the
// "<" / ">" keys). The choice is remembered per browser; localStorage can
// throw (sandboxed iframes, blocked storage) or be missing. A rate set
// outside the button (iOS native fullscreen's speed menu, e.g. 0.5) is
// adopted (ratechange) if it is between 0.25 and 4.
export const SPEEDS = [1, 1.25, 1.5, 2, 0.75]
export const STORAGE_KEY = "vips-playback-rate"
const STEPS = [...SPEEDS].sort((a, b) => a - b)
const usableRate = (r) => typeof r === "number" && Number.isFinite(r) && r >= 0.25 && r <= 4

export class PlaybackSpeed {
  constructor(video, button, storage = () => window.localStorage) {
    this.video = video
    this.button = button
    this.storage = storage
    this.rate = 1
    // A media load (hls.js attach, a new source) resets playbackRate.
    video.addEventListener("loadedmetadata", () => {
      if (video.playbackRate !== this.rate) video.playbackRate = this.rate
    })
    // A rate changed elsewhere becomes the speed, so loadedmetadata keeps it.
    // Only with metadata loaded: a reset during a media load is the browser's.
    video.addEventListener("ratechange", () => {
      if (video.readyState >= 1 && video.playbackRate !== this.rate && usableRate(video.playbackRate)) this.set(video.playbackRate)
    })
    this.set(this.stored(), false)
  }

  stored() {
    try {
      const rate = parseFloat(this.storage().getItem(STORAGE_KEY))
      return usableRate(rate) ? rate : 1
    } catch (e) {
      return 1
    }
  }

  set(rate, remember = true) {
    this.rate = rate
    this.video.defaultPlaybackRate = rate
    this.video.playbackRate = rate
    if (this.button) {
      this.button.textContent = `${rate}×`
      this.button.setAttribute("aria-label", `Playback speed ${rate}×`)
    }
    if (remember) {
      try { this.storage().setItem(STORAGE_KEY, String(rate)) } catch (e) { /* not remembered */ }
    }
  }

  cycle() {
    this.set(SPEEDS[(SPEEDS.indexOf(this.rate) + 1) % SPEEDS.length])
  }

  // The next step above (dir 1) or below (dir -1) the current rate, which may
  // be an adopted rate between two steps; stops at the ends.
  step(dir) {
    const next = dir > 0 ? STEPS.find((s) => s > this.rate) : [...STEPS].reverse().find((s) => s < this.rate)
    this.set(next ?? (dir > 0 ? STEPS[STEPS.length - 1] : STEPS[0]))
  }
}
